import React, { useState } from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey } from '../../design-system/theme';

// Device dimensions
const DEVICE_SPECS = {
  mobile: { width: 375, height: 667, label: 'Mobile', icon: '📱' },
  tablet: { width: 768, height: 1024, label: 'Tablet', icon: '📋' },
  desktop: { width: 1280, height: 800, label: 'Desktop', icon: '🖥️' },
} as const;

type DeviceType = keyof typeof DEVICE_SPECS;
type Orientation = 'portrait' | 'landscape';

interface DevicePreviewFrameProps {
  children: React.ReactNode;
  defaultDevice?: DeviceType;
  showControls?: boolean;
  maxHeight?: number;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: ${blueGrey[1]};
  border-radius: 8px;
`;

const DeviceButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 2px solid ${props => props.$active ? theme.primary : neutral[3]};
  border-radius: 8px;
  background: ${props => props.$active ? theme.primary : 'white'};
  color: ${props => props.$active ? 'white' : theme.textPrimary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.primary};
    background: ${props => props.$active ? theme.primary : blueGrey[1]};
  }
  
  .icon {
    font-size: 16px;
  }
`;

const OrientationButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 2px solid ${props => props.$active ? theme.primary : neutral[3]};
  border-radius: 6px;
  background: ${props => props.$active ? theme.primary : 'white'};
  color: ${props => props.$active ? 'white' : theme.textPrimary};
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: ${neutral[3]};
  margin: 0 8px;
`;

const PreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 24px;
  background: ${blueGrey[2]};
  border-radius: 12px;
  overflow: auto;
  min-height: 400px;
`;

const DeviceFrame = styled.div<{ 
  $width: number; 
  $height: number; 
  $scale: number;
  $device: DeviceType;
}>`
  position: relative;
  width: ${props => props.$width}px;
  height: ${props => props.$height}px;
  transform: scale(${props => props.$scale});
  transform-origin: top center;
  background: white;
  border-radius: ${props => props.$device === 'mobile' ? '32px' : props.$device === 'tablet' ? '24px' : '8px'};
  box-shadow: 
    0 0 0 ${props => props.$device === 'mobile' ? '12px' : props.$device === 'tablet' ? '16px' : '4px'} ${blueGrey[8]},
    0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  
  /* Device-specific styling */
  ${props => props.$device === 'mobile' && `
    &::before {
      content: '';
      position: absolute;
      top: 8px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 6px;
      background: ${blueGrey[6]};
      border-radius: 3px;
      z-index: 10;
    }
  `}
  
  ${props => props.$device === 'tablet' && `
    &::before {
      content: '';
      position: absolute;
      top: 12px;
      left: 50%;
      transform: translateX(-50%);
      width: 8px;
      height: 8px;
      background: ${blueGrey[5]};
      border-radius: 50%;
      z-index: 10;
    }
  `}
`;

const FrameContent = styled.div<{ $device: DeviceType }>`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-top: ${props => props.$device === 'mobile' ? '24px' : props.$device === 'tablet' ? '32px' : '0'};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${neutral[2]};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${neutral[4]};
    border-radius: 3px;
  }
`;

const DimensionsLabel = styled.div`
  text-align: center;
  font-size: 12px;
  color: ${theme.textSecondary};
  margin-top: 8px;
`;

export function DevicePreviewFrame({
  children,
  defaultDevice = 'mobile',
  showControls = true,
  maxHeight = 600,
}: DevicePreviewFrameProps) {
  const [device, setDevice] = useState<DeviceType>(defaultDevice);
  const [orientation, setOrientation] = useState<Orientation>('portrait');
  
  const spec = DEVICE_SPECS[device];
  const isLandscape = orientation === 'landscape';
  
  // Calculate dimensions based on orientation
  const frameWidth = isLandscape ? spec.height : spec.width;
  const frameHeight = isLandscape ? spec.width : spec.height;
  
  // Calculate scale to fit in container
  const scale = Math.min(1, maxHeight / frameHeight, 0.9);
  
  return (
    <Container>
      {showControls && (
        <ControlBar>
          {(Object.keys(DEVICE_SPECS) as DeviceType[]).map((deviceType) => (
            <DeviceButton
              key={deviceType}
              $active={device === deviceType}
              onClick={() => setDevice(deviceType)}
            >
              <span className="icon">{DEVICE_SPECS[deviceType].icon}</span>
              {DEVICE_SPECS[deviceType].label}
            </DeviceButton>
          ))}
          
          <Divider />
          
          <OrientationButton
            $active={orientation === 'portrait'}
            onClick={() => setOrientation('portrait')}
            title="Portrait"
            disabled={device === 'desktop'}
          >
            📱
          </OrientationButton>
          <OrientationButton
            $active={orientation === 'landscape'}
            onClick={() => setOrientation('landscape')}
            title="Landscape"
            disabled={device === 'desktop'}
          >
            📺
          </OrientationButton>
        </ControlBar>
      )}
      
      <PreviewContainer>
        <div>
          <DeviceFrame 
            $width={frameWidth} 
            $height={frameHeight} 
            $scale={scale}
            $device={device}
          >
            <FrameContent $device={device}>
              {children}
            </FrameContent>
          </DeviceFrame>
          <DimensionsLabel>
            {frameWidth} × {frameHeight}px {scale < 1 && `(${Math.round(scale * 100)}% scale)`}
          </DimensionsLabel>
        </div>
      </PreviewContainer>
    </Container>
  );
}

export default DevicePreviewFrame;
